import { useMemo } from 'react';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import { Pie } from 'react-chartjs-2';
import { marketSegmentation } from '../../../services/tycoon/marketSegmentationService';
import type { Product } from '@smartphone-tycoon/shared-core';

ChartJS.register(ArcElement, Tooltip, Legend);

interface MarketSegmentsProps {
  products: Product[];
}

export function MarketSegments({ products }: MarketSegmentsProps) {
  const segments = marketSegmentation.getSegments();
  
  // Mock data for sales by segment - in a real implementation, this would come from 
  // the sales tracking system or be calculated based on product attributes
  const playerSalesBySegment = useMemo(() => {
    const salesData = new Map<string, number>();
    
    // Initialize all segments with 0
    segments.forEach((segment, segmentId) => {
      salesData.set(segmentId, 0);
    });
    
    // Distribute sales based on product characteristics
    products.forEach(product => {
      if (product.status === 'production' && product.salesMetrics.unitsSold > 0) {
        const totalUnits = product.salesMetrics.unitsSold;
        
        // Determine primary segment based on price
        if (product.price < 350) {
          salesData.set('budget_conscious', (salesData.get('budget_conscious') || 0) + totalUnits * 0.6);
          salesData.set('value_seekers', (salesData.get('value_seekers') || 0) + totalUnits * 0.3);
          salesData.set('senior_users', (salesData.get('senior_users') || 0) + totalUnits * 0.1);
        } else if (product.price < 700) {
          salesData.set('value_seekers', (salesData.get('value_seekers') || 0) + totalUnits * 0.5);
          salesData.set('tech_enthusiasts', (salesData.get('tech_enthusiasts') || 0) + totalUnits * 0.2);
          salesData.set('enterprise_professional', (salesData.get('enterprise_professional') || 0) + totalUnits * 0.2);
          salesData.set('budget_conscious', (salesData.get('budget_conscious') || 0) + totalUnits * 0.1);
        } else {
          salesData.set('premium_users', (salesData.get('premium_users') || 0) + totalUnits * 0.5);
          salesData.set('tech_enthusiasts', (salesData.get('tech_enthusiasts') || 0) + totalUnits * 0.3);
          salesData.set('enterprise_professional', (salesData.get('enterprise_professional') || 0) + totalUnits * 0.2);
        }
      }
    });
    
    return salesData;
  }, [products, segments]);
  
  // Mock market-wide sales data
  const totalMarketSalesBySegment = useMemo(() => {
    const marketData = new Map<string, number>();
    
    segments.forEach((segment, segmentId) => {
      // Simulate market sales based on segment size and characteristics
      const baseSales = segment.size * 0.15; // TODO: Make replacement rate configurable
      marketData.set(segmentId, baseSales);
    });
    
    return marketData;
  }, [segments]);
  
  // Calculate percentages for player sales
  const playerSalesData = useMemo(() => {
    const total = Array.from(playerSalesBySegment.values()).reduce((sum, val) => sum + val, 0);
    const labels: string[] = [];
    const data: number[] = [];
    const percentages: number[] = [];
    
    segments.forEach((segment, segmentId) => {
      const sales = playerSalesBySegment.get(segmentId) || 0;
      if (sales > 0) {
        labels.push(segment.name);
        data.push(sales);
        percentages.push(total > 0 ? (sales / total) * 100 : 0);
      }
    });
    
    return { labels, data, percentages, total };
  }, [segments, playerSalesBySegment]);
  
  // Calculate percentages for market sales
  const marketSalesData = useMemo(() => {
    const total = Array.from(totalMarketSalesBySegment.values()).reduce((sum, val) => sum + val, 0);
    const labels: string[] = [];
    const data: number[] = [];
    const percentages: number[] = [];
    
    segments.forEach((segment, segmentId) => {
      const sales = totalMarketSalesBySegment.get(segmentId) || 0;
      labels.push(segment.name);
      data.push(sales);
      percentages.push(total > 0 ? (sales / total) * 100 : 0);
    });
    
    return { labels, data, percentages, total };
  }, [segments, totalMarketSalesBySegment]);
  
  const chartColors = [
    'rgba(59, 130, 246, 0.8)',   // Blue
    'rgba(34, 197, 94, 0.8)',    // Green
    'rgba(168, 85, 247, 0.8)',   // Purple
    'rgba(251, 146, 60, 0.8)',   // Orange
    'rgba(236, 72, 153, 0.8)',   // Pink
    'rgba(250, 204, 21, 0.8)',   // Yellow
  ];
  
  const playerChartData = {
    labels: playerSalesData.labels,
    datasets: [{
      data: playerSalesData.data,
      backgroundColor: chartColors.slice(0, playerSalesData.labels.length),
      borderColor: chartColors.slice(0, playerSalesData.labels.length).map(color => color.replace('0.8', '1')),
      borderWidth: 2
    }]
  };
  
  const marketChartData = {
    labels: marketSalesData.labels,
    datasets: [{
      data: marketSalesData.data,
      backgroundColor: chartColors,
      borderColor: chartColors.map(color => color.replace('0.8', '1')),
      borderWidth: 2
    }]
  };
  
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom' as const,
        labels: {
          padding: 15,
          font: {
            size: 11
          }
        }
      },
      tooltip: {
        callbacks: {
          label: (context: { label?: string; parsed?: number; dataset: { data: number[] } }) => {
            const label = context.label || '';
            const value = context.parsed || 0;
            const percentage = context.dataset.data.reduce((a: number, b: number) => a + b, 0) > 0
              ? ((value / context.dataset.data.reduce((a: number, b: number) => a + b, 0)) * 100).toFixed(1)
              : '0';
            return `${label}: ${Math.round(value).toLocaleString()} (${percentage}%)`;
          }
        }
      }
    }
  };
  
  return (
    <div className="space-y-6">
      {/* Market Segments Overview */}
      <div className="bg-theme-bg-content rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold mb-4 text-theme-text-primary">Market Segments Overview</h3>
        <div className="overflow-x-auto">
          <table className="min-w-full table-auto">
            <thead>
              <tr className="border-b">
                <th className="px-4 py-3 text-left font-medium text-theme-text-secondary">Segment</th>
                <th className="px-4 py-3 text-left font-medium text-theme-text-secondary">Market Size</th>
                <th className="px-4 py-3 text-left font-medium text-theme-text-secondary">Growth Rate</th>
                <th className="px-4 py-3 text-left font-medium text-theme-text-secondary">Price Sensitivity</th>
                <th className="px-4 py-3 text-left font-medium text-theme-text-secondary">Tech Adoption</th>
                <th className="px-4 py-3 text-left font-medium text-theme-text-secondary">Age Range</th>
                <th className="px-4 py-3 text-left font-medium text-theme-text-secondary">Income Range</th>
              </tr>
            </thead>
            <tbody>
              {Array.from(segments.entries()).map(([segmentId, segment]) => (
                <tr key={segmentId} className="border-b hover:bg-theme-bg-secondary">
                  <td className="px-4 py-3 font-medium text-theme-text-primary">{segment.name}</td>
                  <td className="px-4 py-3 text-theme-text-primary">{(segment.size / 1000000).toFixed(0)}M</td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${
                      segment.growthRate > 0.1 ? 'bg-theme-success/10 text-theme-success' :
                      segment.growthRate > 0.05 ? 'bg-theme-warning/10 text-theme-warning' :
                      'bg-theme-bg-tertiary text-theme-text-primary'
                    }`}>
                      {(segment.growthRate * 100).toFixed(0)}%
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <div className="w-20 bg-theme-bg-tertiary rounded-full h-2">
                        <div 
                          className="h-2 rounded-full bg-theme-info"
                          style={{ width: `${(segment.priceElasticity / 2) * 100}%` }}
                        />
                      </div>
                      <span className="text-sm text-theme-text-primary">{segment.priceElasticity.toFixed(1)}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${
                      segment.demographics.techAdoption === 'early' ? 'bg-theme-accent/10 text-theme-accent' :
                      segment.demographics.techAdoption === 'mainstream' ? 'bg-theme-info/10 text-theme-info' :
                      'bg-theme-bg-tertiary text-theme-text-primary'
                    }`}>
                      {segment.demographics.techAdoption}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-theme-text-primary">
                    {segment.demographics.ageRange[0]}-{segment.demographics.ageRange[1]}
                  </td>
                  <td className="px-4 py-3 text-sm text-theme-text-primary">
                    ${(segment.demographics.incomeRange[0] / 1000).toFixed(0)}k-${(segment.demographics.incomeRange[1] / 1000).toFixed(0)}k
                  </td>
                </tr>
              ))}
              <tr className="border-t-2 font-semibold text-theme-text-primary">
                <td className="px-4 py-3">Total</td>
                <td className="px-4 py-3">{(Array.from(segments.values()).reduce((sum, segment) => sum + segment.size, 0) / 1000000).toFixed(0)}M</td>
                <td className="px-4 py-3" colSpan={5}></td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
      
      {/* Player Sales by Segment */}
      <div className="bg-theme-bg-content rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold mb-4 text-theme-text-primary">Your Customers by Market Segment</h3>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Table */}
          <div className="overflow-x-auto">
            <table className="min-w-full table-auto">
              <thead>
                <tr className="border-b">
                  <th className="px-4 py-3 text-left font-medium text-theme-text-secondary">Segment</th>
                  <th className="px-4 py-3 text-right font-medium text-theme-text-secondary">Units Sold</th>
                  <th className="px-4 py-3 text-right font-medium text-theme-text-secondary">% of Sales</th>
                </tr>
              </thead>
              <tbody>
                {playerSalesData.labels.map((label, index) => (
                  <tr key={label} className="border-b hover:bg-theme-bg-secondary">
                    <td className="px-4 py-3 text-theme-text-primary">{label}</td>
                    <td className="px-4 py-3 text-right text-theme-text-primary">{Math.round(playerSalesData.data[index]).toLocaleString()}</td>
                    <td className="px-4 py-3 text-right font-medium text-theme-text-primary">{playerSalesData.percentages[index].toFixed(1)}%</td>
                  </tr>
                ))}
                <tr className="border-t-2 font-semibold text-theme-text-primary">
                  <td className="px-4 py-3">Total</td>
                  <td className="px-4 py-3 text-right">{Math.round(playerSalesData.total).toLocaleString()}</td>
                  <td className="px-4 py-3 text-right">100.0%</td>
                </tr>
              </tbody>
            </table>
          </div>
          
          {/* Pie Chart */}
          <div className="h-64">
            {playerSalesData.total > 0 ? (
              <Pie data={playerChartData} options={chartOptions} />
            ) : (
              <div className="flex items-center justify-center h-full text-theme-text-muted">
                <p>No sales data available yet</p>
              </div>
            )}
          </div>
        </div>
      </div>
      
      {/* Total Market Sales by Segment */}
      <div className="bg-theme-bg-content rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold mb-4 text-theme-text-primary">Total Market Sales by Segment</h3>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Table */}
          <div className="overflow-x-auto">
            <table className="min-w-full table-auto">
              <thead>
                <tr className="border-b">
                  <th className="px-4 py-3 text-left font-medium text-theme-text-secondary">Segment</th>
                  <th className="px-4 py-3 text-right font-medium text-theme-text-secondary">Market Sales</th>
                  <th className="px-4 py-3 text-right font-medium text-theme-text-secondary">% of Market</th>
                </tr>
              </thead>
              <tbody>
                {marketSalesData.labels.map((label, index) => (
                  <tr key={label} className="border-b hover:bg-theme-bg-secondary">
                    <td className="px-4 py-3 text-theme-text-primary">{label}</td>
                    <td className="px-4 py-3 text-right text-theme-text-primary">{(marketSalesData.data[index] / 1000000).toFixed(1)}M</td>
                    <td className="px-4 py-3 text-right font-medium text-theme-text-primary">{marketSalesData.percentages[index].toFixed(1)}%</td>
                  </tr>
                ))}
                <tr className="border-t-2 font-semibold text-theme-text-primary">
                  <td className="px-4 py-3">Total</td>
                  <td className="px-4 py-3 text-right">{(marketSalesData.total / 1000000).toFixed(1)}M</td>
                  <td className="px-4 py-3 text-right">100.0%</td>
                </tr>
              </tbody>
            </table>
          </div>
          
          {/* Pie Chart */}
          <div className="h-64">
            <Pie data={marketChartData} options={chartOptions} />
          </div>
        </div>
      </div>
    </div>
  );
}